import 'react-phone-number-input/style.css';

import {
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import PhoneInput from 'react-phone-number-input';
import { Props as PhoneInputProps } from 'react-phone-number-input/input';
import styled from 'styled-components';

import Select, { SelectOptionType } from './Select';

type InputFieldProps = {
  name: string;
  label?: string;
  error?: string | null;
  inputProps: Record<string, any>;
  className?: string;
};

type InputFieldSearchProps = InputFieldProps & {
  withSeparateSearchButton?: boolean;
};

export const InputField = ({
  name,
  label,
  error,
  inputProps,
  className,
}: InputFieldProps) => (
  <InputFieldWrapper className={className} error={error}>
    <InputLabel htmlFor={name}>{label}</InputLabel>
    <InputItemContainer>
      <input id={name} {...inputProps} />
      {error && <ExclamationCircleIcon />}
    </InputItemContainer>
    {error && (
      <ErrorMessage data-cy="input-error-content">{error}</ErrorMessage>
    )}
  </InputFieldWrapper>
);

export const InputFieldSearch = ({
  name,
  label,
  error,
  inputProps,
  className,
  withSeparateSearchButton,
}: InputFieldSearchProps) => (
  <InputFieldWrapper className={className} error={error}>
    <InputLabel htmlFor={name}>{label}</InputLabel>
    <InputSearchContainer>
      <InputSearchItemContainer separateButton={withSeparateSearchButton}>
        <input id={name} {...inputProps} />
        {error && <ExclamationCircleIcon />}
      </InputSearchItemContainer>
      <SearchButton
        id={'searchButton'}
        error={error}
        separateButton={withSeparateSearchButton}
      >
        <MagnifyingGlassIcon />
      </SearchButton>
    </InputSearchContainer>
    {error && <ErrorMessage>{error}</ErrorMessage>}
  </InputFieldWrapper>
);

export const InputSearchBar = styled(InputFieldSearch)`
  width: 40% !important;
  input[type='search']::-webkit-search-cancel-button {
    appearance: searchfield;
    display: inline-block;
    cursor: pointer;
  }
  & #searchButton {
    height: 44px !important;
    & svg {
      width: 25px;
      height: 25px;
    }
  }
`;

type PhoneNumberInputFieldProps = {
  label: string;
  value: string;
  name: string;
  phoneInputProps: PhoneInputProps<
    React.InputHTMLAttributes<HTMLInputElement>
  > & {
    flagComponent?: (props: any) => any;
    numberInputProps?: Record<string, any>;
  };
  errorMessage?: string;
  className?: string;
};

export const PhoneNumberInputField = ({
  label,
  name,
  value,
  phoneInputProps,
  errorMessage,
  className,
}: PhoneNumberInputFieldProps) => (
  <PhoneInputWrapper className={className}>
    <InputLabel htmlFor={name}>{label}</InputLabel>
    <PhoneInputComponent
      value={value}
      {...phoneInputProps}
      error={errorMessage}
    />
    {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
  </PhoneInputWrapper>
);

const FieldItemContainer = styled.div`
  margin: 4px 0 8px;
`;

const InputFieldWrapper = styled.div<{ error?: string | null }>`
  width: 100%;

  input {
    padding: 9px 13px;
    width: 100%;
    border-radius: 6px;
    font-size: 14px;
    border: 1px solid
      ${(props) =>
        props.error
          ? props.theme.colors.errorRed2
          : props.theme.colors.grayDark2};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    box-sizing: border-box;
  }
`;

const InputLabel = styled.label`
  color: ${(props) => props.theme.colors.grayDark3};
`;

const InputItemContainer = styled(FieldItemContainer)`
  position: relative;

  svg {
    position: absolute;
    top: 8px;
    right: 5px;
    width: 20px;
    height: 20px;
    fill: ${(props) => props.theme.colors.errorRed};
  }
`;

export const ErrorMessage = styled.p`
  color: ${(props) => props.theme.colors.errorRed};
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
`;

const InputSearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  input {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
`;

const InputSearchItemContainer = styled(InputItemContainer)<{
  separateButton: boolean | undefined;
}>`
  width: 100%;

  input {
    ${(props) => (props.separateButton ? 'border-right: none!important;' : '')}
  }
`;

const SearchButton = styled.button<{
  error?: string | null;
  separateButton: boolean | undefined;
}>`
  cursor: pointer;
  width: 52px;
  height: 36px;
  border: 1px solid
    ${(props) =>
      props.error
        ? props.theme.colors.errorRed2
        : props.theme.colors.grayDark2};
  background-color: ${(props) =>
    props.separateButton
      ? props.theme.colors.white
      : props.theme.colors.grayLight};
  border-left: none;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  margin: -4px 0 0 0;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    fill: ${(props) => props.theme.colors.grayLight3};
    width: 19px;
    height: 19px;
  }
`;

export const OptionsWrapper = styled.div<{ opened: boolean }>`
  display: ${(props) => (props.opened ? 'block' : 'none')};
  position: absolute;
  top: 40px;
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.grayDark2};
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  overflow: hidden;
  z-index: 10;
`;

export const Option = styled.div`
  padding: 9px 14px;
  appearance: none;
  color: ${(props) => props.theme.colors.grayDark4};
  cursor: pointer;
  min-width: 150px;
  transition: 0.3s ease;

  background-color: ${(props) => props.theme.colors.white};

  &:hover {
    background-color: ${(props) => props.theme.colors.grayDark2};
  }
`;

const PhoneInputWrapper = styled.div`
  width: 100%;
`;

const PhoneInputComponent = styled(PhoneInput as any)<{ error?: string }>`
  border: 1px solid
    ${(props) =>
      props.error
        ? props.theme.colors.errorRed2
        : props.theme.colors.grayDark2};
  border-radius: 6px;
  overflow: hidden;
  margin: 4px 0 8px;

  * {
    box-shadow: none;
    background-color: ${(props) => props.theme.colors.white};
  }

  .PhoneInputCountry {
    padding-left: 13px;
    padding-right: 4px;
    color: ${(props) => props.theme.colors.grayDark};
    font-size: 14px;
    margin: 0;
  }

  input {
    padding: 9px 13px;
    width: 100%;

    font-size: 14px;
    border: none;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    box-sizing: border-box;
  }
`;

type SelectFieldProps = {
  name: string;
  label?: string;
  error?: string | null;
  selectFieldProps: Record<string, any>;
  className?: string;
  options: SelectOptionType[];
};

export const SelectField = ({
  name,
  label,
  error,
  selectFieldProps,
  className,
  options,
}: SelectFieldProps) => (
  <InputFieldWrapper className={className} error={error}>
    <InputLabel htmlFor={name}>{label ?? ''}</InputLabel>
    <FieldItemContainer>
      <Select
        options={options}
        {...{
          ...selectFieldProps,
          selectProps: { ...selectFieldProps.selectProps, id: name },
        }}
      />
    </FieldItemContainer>
    {error && (
      <ErrorMessage data-cy="input-error-content">{error}</ErrorMessage>
    )}
  </InputFieldWrapper>
);

type CheckboxFieldProps = {
  name: string;
  label: string;
  error?: string | null;
  checkboxProps: Record<string, any>;
  className?: string;
};

export const CheckboxField = ({
  name,
  label,
  error,
  checkboxProps,
  className,
}: CheckboxFieldProps) => (
  <CheckboxFieldWrapper className={className} error={error}>
    <CheckboxItemContainer>
      <input id={name} type="checkbox" {...checkboxProps} />
      <CheckboxInputLabel htmlFor={name}>{label}</CheckboxInputLabel>
    </CheckboxItemContainer>
    {error && (
      <Wrapper>
        <CircleWrapper>
          <ExclamationCircleIcon />
        </CircleWrapper>
        <CheckboxErrorMessage data-cy="checkbox-error-content">
          {error}
        </CheckboxErrorMessage>
      </Wrapper>
    )}
  </CheckboxFieldWrapper>
);

const CircleWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;

  svg {
    top: 30px;
    right: 5px;
    width: 20px;
    height: 20px;
    fill: ${(props) => props.theme.colors.errorRed};
  }
`;

const CheckboxFieldWrapper = styled.div<{ error?: string | null }>`
  margin: 20px 0 20px 8px;

  input {
    margin: 4px 10px 0 0;
    padding: 9px 13px;
    position: absolute;
  }
`;

const CheckboxInputLabel = styled.label`
  color: ${(props) => props.theme.colors.grayDark3};
  font-style: normal;
  padding-left: 1.4em;
  display: inline-block;
`;

const CheckboxItemContainer = styled.div`
  position: relative;
  padding-left: 6px;
  margin-right: 10px;
`;

const CheckboxErrorMessage = styled.p`
  margin-bottom: 10px;
  margin-top: 10px;
  width: 85%;
  padding-left: 10px;
  color: ${(props) => props.theme.colors.errorRed};
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
`;
